import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { tr, de, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Building2, MapPin, Star, Calendar, Users, Loader2,
  ArrowLeft, Check, X, RefreshCw, BedDouble, Coffee,
  Info, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { tursysHotels } from '../lib/api';
import { PageSeo } from '@/components/seo/PageSeo';

const HotelDetailPage = () => {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [hotel, setHotel] = useState(null);
  const [offers, setOffers] = useState([]);
  const [searchContext, setSearchContext] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  
  // Date locale
  const getDateLocale = () => {
    switch(i18n.language) {
      case 'tr': return tr;
      case 'de': return de;
      default: return enUS;
    }
  };
  
  useEffect(() => {
    loadHotelDetails();
  }, [hotelId]);
  
  const loadHotelDetails = async () => {
    setLoading(true);
    
    try {
      // Get search context from sessionStorage
      const contextStr = sessionStorage.getItem('hotelSearchContext');
      if (!contextStr) {
        toast.error('Arama bilgisi bulunamadı. Lütfen tekrar arama yapın.');
        navigate('/hotels');
        return;
      }
      
      const context = JSON.parse(contextStr);
      setSearchContext(context);
      
      // Get hotel details from Tursys API
      const response = await tursysHotels.detail({
        hotel_pool_id: hotelId,
        hotel_giata_id: context.hotel?.giataId || '',
        hotel_name: context.hotel?.name || '',
        pool_search_id: context.poolSearchId,
        search_request: context.searchRequest
      });
      
      if (response.data?.success) {
        setHotel(response.data.hotel);
        setOffers(response.data.offers || []);
      } else {
        toast.error('Otel detayları yüklenemedi');
        // Use basic hotel info from context
        setHotel(context.hotel);
      }
      
    } catch (error) {
      console.error('Hotel detail error:', error);
      toast.error('Otel detayları yüklenirken hata oluştu');
      
      // Fallback to context data
      const contextStr = sessionStorage.getItem('hotelSearchContext');
      if (contextStr) {
        const context = JSON.parse(contextStr);
        setSearchContext(context);
        setHotel(context.hotel);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectOffer = (offer) => {
    setSelectedOffer(offer);
    // Store selection for booking
    sessionStorage.setItem('selectedHotelOffer', JSON.stringify({
      hotel,
      offer,
      searchContext
    }));
  };
  
  const handleBookNow = () => {
    if (!selectedOffer) {
      toast.error('Lütfen bir oda seçin');
      return;
    }
    
    // Store selection for booking
    sessionStorage.setItem('selectedHotelOffer', JSON.stringify({
      hotel,
      offer: selectedOffer,
      searchContext
    }));
    
    // Navigate to booking page
    navigate('/hotels/booking');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-indigo-600" />
          <p className="text-lg text-gray-600">{t('hotelDetail.loading') || 'Loading hotel details...'}</p>
        </div>
      </div>
    );
  }
  
  if (!hotel) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg text-gray-600">{t('hotelDetail.notFound') || 'Hotel not found'}</p>
          <Button onClick={() => navigate('/hotels')} className="mt-4">
            {t('hotelDetail.backToSearch') || 'Back to Search'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageSeo
        title={hotel.name}
        description={t('hotelSearch.subtitle')}
        segments={['hotels', hotelId]}
      />
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/hotels')}
            className="mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('hotelDetail.backToResults') || 'Back to Results'}
          </Button>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{hotel.name}</h1>
                <div className="flex">
                  {[...Array(hotel.stars || searchContext?.hotel?.stars || 0)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>
              {searchContext?.hotel?.address && (
                <p className="text-gray-600 flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {searchContext.hotel.address.city}, {searchContext.hotel.address.country}
                </p>
              )}
            </div>
            
            {/* Search Summary */}
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {searchContext?.searchRequest?.check_in && (
                  <>
                    {format(new Date(searchContext.searchRequest.check_in), 'd MMM', { locale: getDateLocale() })}
                    {' - '}
                    {format(new Date(searchContext.searchRequest.check_out), 'd MMM', { locale: getDateLocale() })}
                  </>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {searchContext?.guests?.adults || 2} {t('hotelDetail.guests') || 'guests'}
              </div>
              <div className="text-indigo-600 font-medium">
                {searchContext?.nights || 7} {t('hotelDetail.nights') || 'nights'}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Hotel Image & Info */}
          <div className="lg:col-span-2">
            {/* Hotel Image */}
            <Card className="mb-6 overflow-hidden">
              <div className="aspect-video bg-gray-100 relative">
                {hotel.mainImage || searchContext?.hotel?.mainImage ? (
                  <img 
                    src={hotel.mainImage || searchContext?.hotel?.mainImage} 
                    alt={hotel.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=500&fit=crop';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Building2 className="w-24 h-24 text-gray-300" />
                  </div>
                )}
              </div>
            </Card>
            
            {/* Room Options */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BedDouble className="w-5 h-5" />
                  {t('hotelDetail.roomOptions') || 'Room Options'}
                  <Badge variant="secondary">{offers.length} {t('hotelDetail.options') || 'options'}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {offers.length > 0 ? (
                  <div className="space-y-4">
                    {offers.map((offer, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          selectedOffer?.offerId === offer.offerId 
                            ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' 
                            : 'hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => handleSelectOffer(offer)}
                        data-testid={`room-offer-${idx}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold">{offer.roomName}</h3>
                              {offer.isRefundable && (
                                <Badge className="bg-green-100 text-green-700">
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  {t('hotelDetail.refundable') || 'Refundable'}
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                              <Coffee className="w-4 h-4" />
                              <span>{offer.boardName}</span>
                            </div>
                            
                            {offer.cancellationPolicy && offer.cancellationPolicy.length > 0 && (
                              <div className="text-xs text-gray-500">
                                {offer.isRefundable ? (
                                  <span className="text-green-600 flex items-center gap-1">
                                    <Check className="w-3 h-3" />
                                    {t('hotelDetail.freeCancellation') || 'Free cancellation'} 
                                    {offer.cancellationPolicy[0]?.dueDate && (
                                      <> until {format(new Date(offer.cancellationPolicy[0].dueDate), 'd MMM', { locale: getDateLocale() })}</>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-red-600 flex items-center gap-1">
                                    <X className="w-3 h-3" />
                                    {t('hotelDetail.nonRefundable') || 'Non-refundable'}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="text-right">
                            <div className="text-2xl font-bold text-indigo-600">
                              €{offer.price?.total?.toFixed(0) || 0}
                            </div>
                            <div className="text-sm text-gray-500">
                              €{offer.price?.perNight?.toFixed(0) || 0}/{t('hotelDetail.night') || 'night'}
                            </div>
                            {selectedOffer?.offerId === offer.offerId && (
                              <Badge className="mt-2 bg-indigo-600">
                                <Check className="w-3 h-3 mr-1" />
                                {t('hotelDetail.selected') || 'Selected'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Info className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>{t('hotelDetail.noRoomsAvailable') || 'No rooms available for these dates'}</p>
                    <p className="text-sm mt-2">{t('hotelDetail.tryDifferentDates') || 'Please try different dates'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Right Column - Booking Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>{t('hotelDetail.bookingSummary') || 'Booking Summary'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Hotel Info */}
                  <div className="pb-4 border-b">
                    <h3 className="font-semibold mb-1">{hotel.name}</h3>
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      {searchContext?.hotel?.address?.city || 'Turkey'}
                    </div>
                  </div>
                  
                  {/* Dates */}
                  <div className="pb-4 border-b">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">{t('hotelDetail.checkIn') || 'Check-in'}</span>
                      <span className="font-medium">
                        {searchContext?.searchRequest?.check_in && 
                          format(new Date(searchContext.searchRequest.check_in), 'd MMMM yyyy', { locale: getDateLocale() })}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">{t('hotelDetail.checkOut') || 'Check-out'}</span>
                      <span className="font-medium">
                        {searchContext?.searchRequest?.check_out && 
                          format(new Date(searchContext.searchRequest.check_out), 'd MMMM yyyy', { locale: getDateLocale() })}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('hotelDetail.duration') || 'Duration'}</span>
                      <span className="font-medium">{searchContext?.nights || 7} {t('hotelDetail.nights') || 'nights'}</span>
                    </div>
                  </div>
                  
                  {/* Selected Room */}
                  {selectedOffer && (
                    <div className="pb-4 border-b">
                      <div className="text-sm text-gray-600 mb-2">{t('hotelDetail.selectedRoom') || 'Selected Room'}</div>
                      <div className="font-medium">{selectedOffer.roomName}</div>
                      <div className="text-sm text-gray-600">{selectedOffer.boardName}</div>
                    </div>
                  )}
                  
                  {/* Price */}
                  <div className="pb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('hotelDetail.totalPrice') || 'Total Price'}</span>
                      <span className="text-3xl font-bold text-indigo-600">
                        €{selectedOffer?.price?.total?.toFixed(0) || searchContext?.hotel?.price?.total?.toFixed(0) || 0}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 text-right mt-1">
                      {t('hotelDetail.taxIncluded') || 'Taxes and fees included'}
                    </p>
                  </div>
                  
                  {/* Book Button */}
                  <Button 
                    className="w-full bg-indigo-600 hover:bg-indigo-700 py-6"
                    onClick={handleBookNow}
                    disabled={!selectedOffer}
                    data-testid="book-now-btn"
                  >
                    {t('hotelDetail.bookNow') || 'Book Now'}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                  
                  {!selectedOffer && (
                    <p className="text-sm text-center text-gray-500">
                      {t('hotelDetail.selectRoom') || 'Please select a room to continue'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelDetailPage;
